import { onBeforeUnmount, watchEffect } from 'vue'
import { useRoute } from 'vue-router'
import { buildJsonLd, serializeJsonLd } from './pageMeta.mjs'
import { BRAND_NAME, CONTACT_PAGE_PATH, resolvePage } from './siteContent.mjs'

/**
 * 路由对应的页面定义。获客页也有自己的 title/description，但 noindex，
 * 且不输出 canonical/JSON-LD，避免与公开页抢规范网址。
 * @param {string} path
 * @returns {import('./siteContent.mjs').PageDefinition | null}
 */
function pageForRoutePath(path) {
  // 路由本身就是 .html 实际路径（例如 /about.html），直接用它查注册表。
  return resolvePage(path)
}

/**
 * @param {string} tagName
 * @param {Record<string, string>} attributes
 * @param {string} [text]
 * @returns {HTMLElement}
 */
function element(tagName, attributes, text) {
  const node = document.createElement(tagName)
  Object.entries(attributes).forEach(([name, value]) => node.setAttribute(name, value))
  if (text !== undefined) node.textContent = text
  node.setAttribute('data-page-meta', '')
  return node
}

/**
 * @param {import('./siteContent.mjs').PageDefinition | null} page
 * @param {boolean} noindex
 * @returns {HTMLElement[]}
 */
function metaNodes(page, noindex) {
  const nodes = []
  if (noindex) nodes.push(element('meta', { name: 'robots', content: 'noindex, follow' }))
  if (!page) return nodes

  nodes.push(element('title', {}, page.title), element('meta', { name: 'description', content: page.description }))
  if (noindex) return nodes

  nodes.push(
    element('link', { rel: 'canonical', href: page.canonical }),
    element('meta', { property: 'og:type', content: 'website' }),
    element('meta', { property: 'og:site_name', content: BRAND_NAME }),
    element('meta', { property: 'og:title', content: page.title }),
    element('meta', { property: 'og:description', content: page.description }),
    element('meta', { property: 'og:url', content: page.canonical }),
    element('script', { type: 'application/ld+json' }, serializeJsonLd(buildJsonLd(page)))
  )
  return nodes
}

/**
 * 把当前路由的 title/description/canonical/JSON-LD 同步到 document.head。
 * 预渲染用的浏览器与真实浏览器执行同一段逻辑，因此启 JS 后的 head 与禁 JS 的静态 head 一致。
 * @returns {void}
 */
export function usePageMeta() {
  const route = useRoute()

  watchEffect(() => {
    document.head.querySelectorAll('[data-page-meta]').forEach((node) => node.remove())
    metaNodes(pageForRoutePath(route.path), route.path.startsWith('/add-friend')).forEach((node) => {
      document.head.appendChild(node)
    })
  })

  onBeforeUnmount(() => {
    document.head.querySelectorAll('[data-page-meta]').forEach((node) => node.remove())
  })
}

export { CONTACT_PAGE_PATH }
